import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAgentCommissionsOutput, useUpdateAgentCommissionsOutput } from "../../../../../common/api/queries";
import { ModalSizes } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { useCurrentAgentAccount, useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { CommissionsOutput } from "../../../batch/types";
import { CommissionsCategory } from "../../../enums";
import { CreateUpdateCommissionsOutput } from "../../types";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  commissionOutput?: CommissionsOutput;
};

export const CommissionsOutputModalForm = ({ isOpen, onCancel, commissionOutput }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateCommissionsOutput & { agentId: UUID }>();

  const currentAgent = useCurrentAgentAccount()?.agent;
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const createAgentCommissionsOutput = useCreateAgentCommissionsOutput();
  const updateAgentCommissionsOutput = useUpdateAgentCommissionsOutput();

  useEffect(() => {
    if (isOpen && commissionOutput) {
      form.setFieldsValue({
        agentId: commissionOutput.gainer.id,
        optimisticLockVersion: commissionOutput.optimisticLockVersion,
        category: commissionOutput.category,
        paidCommissionAmount: commissionOutput.paidCommissionAmount,
        note: commissionOutput.note,
      });
    }
  }, [isOpen, commissionOutput, form]);

  const handleFormSubmit = () => {
    form.validateFields().then(({ agentId, ...values }) => {
      if (commissionOutput) {
        updateAgentCommissionsOutput.mutate(
          { id1: agentId, id2: commissionOutput.id, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.outputs.attrs", error.violations),
          },
        );
      } else {
        createAgentCommissionsOutput.mutate(
          { id: agentId, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.outputs.attrs", error.violations),
          },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      title={commissionOutput ? t("commissions.outputs.titles.update") : t("commissions.outputs.titles.create")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createAgentCommissionsOutput.isPending || updateAgentCommissionsOutput.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name={`commissionsOutputModalForm${commissionOutput ? "Update" : "Create"}`}>
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item name="agentId" label={t("commissions.batch.attrs.attachment.gainer")} rules={[validations.notNull]}>
          <AgentSelect optionsProps={{ subordinateTreePath: currentAgent?.accessTreePath }} />
        </Form.Item>

        {isTopAgentPlus ? (
          <Form.Item
            name="category"
            label={t("commissions.enums.commissionsCategory_PLUS._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(CommissionsCategory)
                .filter((category) => category !== CommissionsCategory.PRIMARY)
                .map((category) => ({
                  value: category,
                  label: t(`commissions.enums.commissionsCategory_PLUS.${category}`),
                }))}
            />
          </Form.Item>
        ) : (
          <HiddenInput name="category" initialValue={CommissionsCategory.PRIMARY} />
        )}

        <Form.Item
          name="paidCommissionAmount"
          label={t("commissions.outputs.attrs.paidCommissionAmount")}
          rules={[validations.notNull, validations.minNumber(0.01)]}
        >
          <InputNumber type="decimal" addon="euro" />
        </Form.Item>

        <Form.Item name="note" label={t("commissions.outputs.attrs.note")} rules={[validations.size(1, 8192)]}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
